import axios from 'axios'
import type {
  AnalysisConfig,
  AnalysisResults,
  AudioFile,
  JobOutputsResponse,
  MasteringConfig,
  MasteringPreset,
  ProcessingJob,
  RuntimeCapabilitiesResponse,
  StemSeparationConfig,
  ToolModelCatalog,
  VoiceConversionConfig,
  VoiceModel,
} from '../types'

const API_PATH = '/api/v1'

const sanitizeOrigin = (value?: string): string => {
  if (!value) return ''
  return value.replace(/\/+$/, '')
}

const API_ORIGIN = sanitizeOrigin(import.meta.env.VITE_API_ORIGIN)
const API_BASE_URL = `${API_ORIGIN}${API_PATH}`

const resolveWsOrigin = (): string => {
  const explicitWsOrigin = sanitizeOrigin(import.meta.env.VITE_WS_ORIGIN)
  if (explicitWsOrigin) {
    return explicitWsOrigin
  }

  if (API_ORIGIN) {
    if (API_ORIGIN.startsWith('https://')) {
      return API_ORIGIN.replace('https://', 'wss://')
    }
    if (API_ORIGIN.startsWith('http://')) {
      return API_ORIGIN.replace('http://', 'ws://')
    }
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}`
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

export const getApiBaseUrl = () => API_BASE_URL

export const normalizeApiError = (error: any): string => {
  if (error?.response?.data?.detail) return String(error.response.data.detail)
  if (error?.response?.data?.error) return String(error.response.data.error)
  if (error?.message?.includes?.('Network Error')) {
    return 'Cannot connect to the processing API. Check your backend origin configuration.'
  }
  return error?.message || 'Request failed'
}

// Upload API
export const uploadFile = async (
  file: File,
  role: 'input' | 'reference' = 'input'
): Promise<AudioFile> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await api.post(`/upload?role=${role}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const uploadBatch = async (
  files: File[],
  role: 'input' | 'reference' = 'input'
): Promise<AudioFile[]> => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  const response = await api.post(`/upload/batch?role=${role}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

export const listFiles = async (role?: 'input' | 'reference'): Promise<AudioFile[]> => {
  const suffix = role ? `?role=${role}` : ''
  const response = await api.get(`/files${suffix}`)
  return response.data
}

export const getSystemCapabilities = async (): Promise<RuntimeCapabilitiesResponse> => {
  const response = await api.get('/system/capabilities')
  return response.data
}

export const getStemModelCatalog = async (): Promise<ToolModelCatalog> => {
  const response = await api.get('/models/stems')
  return response.data
}

export const getMasteringModelCatalog = async (): Promise<ToolModelCatalog> => {
  const response = await api.get('/models/mastering')
  return response.data
}

export const getAnalysisModelCatalog = async (): Promise<ToolModelCatalog> => {
  const response = await api.get('/models/analysis')
  return response.data
}

export const getVoiceModelCatalog = async (): Promise<ToolModelCatalog> => {
  const response = await api.get('/models/voice')
  return response.data
}

// Stem Separation API
export const separateStems = async (
  fileId: string,
  config: StemSeparationConfig
): Promise<ProcessingJob> => {
  const response = await api.post(`/process/stems?file_id=${fileId}`, config)
  return response.data
}

// Mastering API
export const getMasteringPresets = async (): Promise<MasteringPreset[]> => {
  const response = await api.get('/mastering/presets')
  return response.data
}

export const masterAudio = async (
  fileId: string,
  config: MasteringConfig
): Promise<ProcessingJob> => {
  const response = await api.post(`/process/mastering?file_id=${fileId}`, config)
  return response.data
}

// Analysis API
export const analyzeAudio = async (
  fileId: string,
  config: AnalysisConfig
): Promise<ProcessingJob> => {
  const response = await api.post(`/process/analyze?file_id=${fileId}`, config)
  return response.data
}

export const getAnalysisResults = async (jobId: string): Promise<AnalysisResults> => {
  const response = await api.get(`/analysis/${jobId}/results`)
  return response.data
}

// Voice Conversion API
export const getVoiceModels = async (): Promise<VoiceModel[]> => {
  const response = await api.get('/voice/models')
  return response.data
}

export const convertVoice = async (
  fileId: string,
  config: VoiceConversionConfig
): Promise<ProcessingJob> => {
  const response = await api.post(`/process/voice?file_id=${fileId}`, config)
  return response.data
}

// Job Management API
export const listJobs = async (limit = 100): Promise<ProcessingJob[]> => {
  const response = await api.get(`/jobs?limit=${limit}`)
  return response.data
}

export const getJob = async (jobId: string): Promise<ProcessingJob> => {
  const response = await api.get(`/jobs/${jobId}`)
  return response.data
}

export const getJobOutputs = async (jobId: string): Promise<JobOutputsResponse> => {
  const response = await api.get(`/jobs/${jobId}/outputs`)
  return response.data
}

export const deleteJob = async (jobId: string): Promise<void> => {
  await api.delete(`/jobs/${jobId}`)
}

// WebSocket for real-time progress
export const createProgressWebSocket = (jobId: string): WebSocket => {
  const wsBase = resolveWsOrigin()
  const wsUrl = `${wsBase}${API_PATH}/ws/progress/${jobId}`
  return new WebSocket(wsUrl)
}

// Download API
export const getDownloadUrl = (filename: string): string => {
  const normalized = filename.split('/').pop() || filename
  return `${API_BASE_URL}/download/${encodeURIComponent(normalized)}`
}

export default api
